import { useState } from 'react';
import { Button, Chip, IconButton, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../shared/components/ErrorState';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { MonthFilterField, fromMonthValue, toMonthValue } from '../../../shared/components/MonthFilterField';
import { useEmployeeNotasFiscaisHooks } from './hooks';
import { EmployeeNotaFiscalFormDialog } from './EmployeeNotaFiscalFormDialog';
import { NotaFiscalUploadDialog } from './NotaFiscalUploadDialog';
import { NotaFiscalReprovarDialog } from './NotaFiscalReprovarDialog';
import type { EmployeeNotaFiscalDto, EmployeeNotaFiscalListItem, NotaFiscalStatus } from './types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'dataCadastro',
  sortOrder: 'desc',
};

const STATUS_FILTER_OPTIONS: NotaFiscalStatus[] = ['NSO', 'SOL', 'PAP', 'APR', 'REP', 'CAN'];

export interface EmployeeNotasFiscaisManagerProps {
  employeeId: number;
}

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

function statusChipColor(
  status: NotaFiscalStatus,
): 'default' | 'success' | 'warning' | 'error' | 'info' {
  switch (status) {
    case 'NSO':
      return 'default';
    case 'SOL':
      return 'info';
    case 'PAP':
      return 'warning';
    case 'APR':
    case 'CON':
      return 'success';
    case 'REP':
      return 'error';
    case 'CAN':
      return 'default';
  }
}

export function EmployeeNotasFiscaisManager({
  employeeId,
}: EmployeeNotasFiscaisManagerProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    query,
    setQuery,
    filters,
    patchFilters,
    clear,
  } = usePersistedGridState(`colaborador-nf:${employeeId}`, DEFAULT_QUERY, {
    status: '' as NotaFiscalStatus | '',
    mes: '' as number | '',
    ano: '' as number | '',
  });
  const statusFiltro = filters.status;
  const mesFiltro = filters.mes;
  const anoFiltro = filters.ano;
  const competenciaCompleta = typeof mesFiltro === 'number' && typeof anoFiltro === 'number';
  const setStatusFiltro = (status: NotaFiscalStatus | '') => patchFilters({ status });
  const setCompetencia = (value: string): void => {
    const parsed = fromMonthValue(value);
    patchFilters({ mes: parsed.month ?? '', ano: parsed.year ?? '' });
  };
  const [editing, setEditing] = useState<EmployeeNotaFiscalDto | null | 'new'>(null);
  const [uploadFor, setUploadFor] = useState<EmployeeNotaFiscalListItem | null>(null);
  const [reprovarFor, setReprovarFor] = useState<EmployeeNotaFiscalListItem | null>(null);
  const [pendingDelete, setPendingDelete] = useState<EmployeeNotaFiscalListItem | null>(null);
  const [pendingSolicitar, setPendingSolicitar] = useState<EmployeeNotaFiscalListItem | null>(null);
  const [pendingAprovar, setPendingAprovar] = useState<EmployeeNotaFiscalListItem | null>(null);

  const hooks = useEmployeeNotasFiscaisHooks(employeeId, {
    statusNotaFiscal: statusFiltro || undefined,
    mes: competenciaCompleta ? mesFiltro : undefined,
    ano: competenciaCompleta ? anoFiltro : undefined,
  });
  const listQuery = hooks.useList(query);
  const deleteMut = hooks.useDelete();
  const solicitarMut = hooks.useSolicitar();
  const aprovarMut = hooks.useAprovar();
  const downloadMut = hooks.useDownloadFile();

  async function openEdit(row: EmployeeNotaFiscalListItem): Promise<void> {
    try {
      setEditing(await hooks.api.get(row.id));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await deleteMut.mutateAsync(pendingDelete.id);
      toast.success(t('features.colaboradores.notasFiscais.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmSolicitar(): Promise<void> {
    if (!pendingSolicitar) return;
    try {
      await solicitarMut.mutateAsync(pendingSolicitar.id);
      toast.success(t('features.colaboradores.notasFiscais.toasts.solicitada'));
      setPendingSolicitar(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmAprovar(): Promise<void> {
    if (!pendingAprovar) return;
    try {
      await aprovarMut.mutateAsync(pendingAprovar.id);
      toast.success(t('features.colaboradores.notasFiscais.toasts.aprovada'));
      setPendingAprovar(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function downloadPdf(row: EmployeeNotaFiscalListItem): Promise<void> {
    try {
      const blob = await downloadMut.mutateAsync(row.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Libera após algum tempo — janela já consumiu o objeto.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<EmployeeNotaFiscalListItem>[] = [
    {
      field: 'periodo',
      headerName: t('features.colaboradores.notasFiscais.columns.periodo'),
      width: 110,
    },
    {
      field: 'clienteNome',
      headerName: t('features.colaboradores.notasFiscais.columns.cliente'),
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'valorReceberFormula',
      headerName: t('features.colaboradores.notasFiscais.columns.valor'),
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number) =>
        typeof value === 'number' && Number.isFinite(value) ? formatBRL(value) : '—',
    },
    {
      field: 'status',
      headerName: t('features.colaboradores.notasFiscais.columns.status'),
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Chip
          size="small"
          label={t(`features.colaboradores.notasFiscais.status.${params.row.status}`)}
          color={statusChipColor(params.row.status)}
          variant={params.row.status === 'APR' ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'dataCadastro',
      headerName: t('features.colaboradores.notasFiscais.columns.dataCadastro'),
      width: 130,
      valueFormatter: (value: string) => {
        const d = dayjs(value);
        return d.isValid() ? d.format('DD/MM/YYYY') : '—';
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 250,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const s = params.row.status;
        const editable = s === 'NSO';
        const canSolicitar = s === 'NSO' || s === 'SOL' || s === 'REP';
        const canUpload = s === 'SOL';
        const canAprovar = s === 'PAP';
        const canReprovar = s === 'PAP';
        const hasPdf = Boolean(params.row.pdfNome);
        const canDelete = s === 'NSO' && !params.row.existeNotaEmpresa;
        return (
          <Stack direction="row" spacing={0.25}>
            <Tooltip
              title={
                editable
                  ? t('common.edit')
                  : t('features.colaboradores.notasFiscais.actions.editDisabledTooltip')
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={!editable}
                  aria-label={t('features.colaboradores.notasFiscais.actions.edit', {
                    id: params.row.id,
                  })}
                  onClick={() => void openEdit(params.row)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={
                canSolicitar
                  ? t('features.colaboradores.notasFiscais.actions.solicitarTooltip')
                  : t('features.colaboradores.notasFiscais.actions.solicitarDisabledTooltip')
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={!canSolicitar}
                  aria-label={t('features.colaboradores.notasFiscais.actions.solicitar', {
                    id: params.row.id,
                  })}
                  onClick={() => setPendingSolicitar(params.row)}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={
                canUpload
                  ? t('features.colaboradores.notasFiscais.actions.uploadTooltip')
                  : t('features.colaboradores.notasFiscais.actions.uploadDisabledTooltip')
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={!canUpload}
                  aria-label={t('features.colaboradores.notasFiscais.actions.upload', {
                    id: params.row.id,
                  })}
                  onClick={() => setUploadFor(params.row)}
                >
                  <UploadFileIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={
                hasPdf
                  ? t('features.colaboradores.notasFiscais.actions.downloadTooltip')
                  : t('features.colaboradores.notasFiscais.actions.downloadDisabledTooltip')
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={!hasPdf || downloadMut.isPending}
                  aria-label={t('features.colaboradores.notasFiscais.actions.download', {
                    id: params.row.id,
                  })}
                  onClick={() => void downloadPdf(params.row)}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={
                canAprovar
                  ? t('features.colaboradores.notasFiscais.actions.aprovarTooltip')
                  : t('features.colaboradores.notasFiscais.actions.aprovarDisabledTooltip')
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="success"
                  disabled={!canAprovar}
                  aria-label={t('features.colaboradores.notasFiscais.actions.aprovar', {
                    id: params.row.id,
                  })}
                  onClick={() => setPendingAprovar(params.row)}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={
                canReprovar
                  ? t('features.colaboradores.notasFiscais.actions.reprovarTooltip')
                  : t('features.colaboradores.notasFiscais.actions.reprovarDisabledTooltip')
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="warning"
                  disabled={!canReprovar}
                  aria-label={t('features.colaboradores.notasFiscais.actions.reprovar', {
                    id: params.row.id,
                  })}
                  onClick={() => setReprovarFor(params.row)}
                >
                  <BlockIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={
                canDelete
                  ? t('common.delete')
                  : t('features.colaboradores.notasFiscais.actions.deleteDisabledTooltip')
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={!canDelete}
                  aria-label={t('features.colaboradores.notasFiscais.actions.delete', {
                    id: params.row.id,
                  })}
                  onClick={() => setPendingDelete(params.row)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <ErrorState
        description={describeApiError(listQuery.error)}
        onRetry={() => listQuery.refetch()}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-end' }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            size="small"
            label={t('features.colaboradores.notasFiscais.filters.status')}
            value={statusFiltro}
            onChange={(e) => setStatusFiltro((e.target.value as NotaFiscalStatus | '') ?? '')}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">
              {t('features.colaboradores.notasFiscais.filters.statusTodos')}
            </MenuItem>
            {STATUS_FILTER_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {t(`features.colaboradores.notasFiscais.status.${s}`)}
              </MenuItem>
            ))}
          </TextField>
          <MonthFilterField
            label={t('components.monthFilter.competencia')}
            value={toMonthValue(anoFiltro, mesFiltro)}
            onChange={setCompetencia}
          />
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
          {t('features.colaboradores.notasFiscais.actions.new')}
        </Button>
      </Stack>

      <DataTable<EmployeeNotaFiscalListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.colaboradores.notasFiscais.searchPlaceholder')}
      />

      <EmployeeNotaFiscalFormDialog
        open={editing !== null}
        nota={editing === 'new' ? null : editing}
        hooks={{ useCreate: hooks.useCreate, useUpdate: hooks.useUpdate }}
        onClose={() => setEditing(null)}
      />

      <NotaFiscalUploadDialog
        open={uploadFor !== null}
        nota={uploadFor}
        useUploadFile={hooks.useUploadFile}
        onClose={() => setUploadFor(null)}
      />

      <NotaFiscalReprovarDialog
        open={reprovarFor !== null}
        nota={reprovarFor}
        useReprovar={hooks.useReprovar}
        onClose={() => setReprovarFor(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.colaboradores.notasFiscais.deleteConfirm.title')}
        description={t('features.colaboradores.notasFiscais.deleteConfirm.description', {
          periodo: pendingDelete?.periodo ?? '',
          cliente: pendingDelete?.clienteNome ?? '',
        })}
        confirmLabel={t('common.delete')}
        loading={deleteMut.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={pendingSolicitar !== null}
        title={t('features.colaboradores.notasFiscais.solicitarConfirm.title')}
        description={t(
          pendingSolicitar?.status === 'NSO'
            ? 'features.colaboradores.notasFiscais.solicitarConfirm.descriptionNSO'
            : 'features.colaboradores.notasFiscais.solicitarConfirm.descriptionResolicitar',
          {
            periodo: pendingSolicitar?.periodo ?? '',
            cliente: pendingSolicitar?.clienteNome ?? '',
          },
        )}
        confirmLabel={t('features.colaboradores.notasFiscais.actions.confirmSolicitar')}
        loading={solicitarMut.isPending}
        destructive={false}
        onConfirm={confirmSolicitar}
        onCancel={() => setPendingSolicitar(null)}
      />

      <ConfirmDialog
        open={pendingAprovar !== null}
        title={t('features.colaboradores.notasFiscais.aprovarConfirm.title')}
        description={t('features.colaboradores.notasFiscais.aprovarConfirm.description', {
          periodo: pendingAprovar?.periodo ?? '',
          cliente: pendingAprovar?.clienteNome ?? '',
        })}
        confirmLabel={t('features.colaboradores.notasFiscais.actions.confirmAprovar')}
        loading={aprovarMut.isPending}
        destructive={false}
        onConfirm={confirmAprovar}
        onCancel={() => setPendingAprovar(null)}
      />
    </Stack>
  );
}
